import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ListColumnConfig } from 'app/main/components/list-page-config/list-page.config';
import { EditUserComponent } from 'app/main/pages/users/edit-user/edit-user.component';
import { ModalService } from 'app/main/shared/services/modal.service';
import { StatusUtils } from 'app/utils/status-utils';
export interface ListCardItem {
  id: string | number;
  name: string;
  organization: string;
  role: string;
  email: string;
  lastLoginDate: string;
  avatar?: string;
  userStatus?: number;
  patientStatus?: number;
  // Patient Specific
  patientID?: string;
  pid?: string;
  age?: number;
  gender?: string;
  birthDate?: string;
  phone?: string;
  location?: string;
  createdDate?: string;
  organizationCode?: string;
  woundType?: number;
  woundStatus?: number;
  woundCategoryType?: number;
  appointmentStatus?: number;
}

import { TranslateModule } from '@ngx-translate/core';
import { PermissionService } from 'app/core/services/user/permission.service';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';

@Component({
  selector: 'app-list-card',
  standalone: true,
  imports: [CommonModule, DatePipe, TranslateModule, HasClaimDirective],
  templateUrl: './list-card.component.html',
  styleUrls: ['./list-card.component.scss'],
})
export class ListCardComponent {
  @Output() userUpdated = new EventEmitter<void>();
  @Input() item!: ListCardItem;

  @Input() columns: ListColumnConfig[] = [];
  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;
  @Input() assignToMe: boolean = false;

  @Output() cardClick = new EventEmitter<ListCardItem>();
  @Output() edit = new EventEmitter<ListCardItem>();
  @Output() delete = new EventEmitter<ListCardItem>();
  @Output() statusToggle = new EventEmitter<ListCardItem>();
  @Output() assign = new EventEmitter<ListCardItem>();

  @Output() assignToMeClick = new EventEmitter<any>();
  @Output() assignModal = new EventEmitter<any>();

  constructor(
    private modalService: ModalService,
    public permissionService: PermissionService,
  ) {}

  openEditUserModal(userId: string): void {
    const modalRef = this.modalService.openComponentModal(EditUserComponent, {
      size: 'lg',
      backdrop: true,
      keyboard: true,
      data: { userId },
    });

    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.userUpdated.emit();
      }
    });
  }

  onCardClick() {
    this.cardClick.emit(this.item);
  }

  onEdit(event: MouseEvent) {
    event.stopPropagation();
    this.edit.emit(this.item);
  }

  onDelete(event: MouseEvent) {
    event.stopPropagation();
    this.delete.emit(this.item);
  }

  onStatusToggle(event: MouseEvent) {
    event.stopPropagation();
    this.statusToggle.emit(this.item);
  }

  onAssign(event: MouseEvent) {
    event.stopPropagation();
    this.assign.emit(this.item);
  }

  openAssignModal(event: MouseEvent) {
    event.stopPropagation();
    this.assignModal.emit(this.item);
  }

  getUserStatusTextClass(status: number): string {
    return StatusUtils?.getUserStatusClass(status);
  }

  getUserStatusText(status: number): string {
    return StatusUtils?.getUserStatusText(String(status));
  }

  getCaseStatusClass(status: number): string {
    return StatusUtils?.getCaseStatusClass(status);
  }

  getCaseStatusText(status: number): string {
    return StatusUtils?.getCaseStatusText(status);
  }

  getAppointmentStatusClass(status: number): string {
    if (status === 1) {
      return '';
    }
    return this.getCaseStatusClass(status);
  }

  getAppointmentStatusText(status: number): string {
    if (status === 1) {
      return 'waiting';
    }
    return this.getCaseStatusText(status);
  }

  getWoundTypeText(woundType: number | undefined): string {
    switch (Number(woundType)) {
      case 0:
        return 'woundTypes.notExist';
      case 1:
        return 'woundTypes.exist';
      default:
        return '-';
    }
  }

  getWoundCategoryText(category: number | undefined): string {
    switch (Number(category)) {
      case 0:
        return 'woundCategories.initial';
      case 1:
        return 'woundCategories.followUp';
      default:
        return '-';
    }
  }

  getWoundStatusText(woundStatus: number | undefined): string {
    switch (Number(woundStatus)) {
      case 0:
        return 'woundStatuses.created';
      case 1:
        return 'woundStatuses.waitingDoctorAssignment';
      case 2:
        return 'woundStatuses.doctorAssigned';
      case 3:
        return 'woundStatuses.treatmentDecisionCreated';
      case 4:
        return 'woundStatuses.treatmentCompleted';
      default:
        return '-';
    }
  }

  getWoundStatusClass(woundStatus: number | undefined): string {
    switch (Number(woundStatus)) {
      case 0:
      case 1:
        return 'status-secondary';
      case 2:
      case 3:
        return 'status-warning';
      case 4:
        return 'status-success';
      default:
        return '';
    }
  }

  getConsultationStatusClass(status: number): string {
    switch (status) {
      case 0:
        return 'status-warning';
      case 1:
        return 'status-info';
      case 2:
        return 'status-primary';
      default:
        return 'status-secondary';
    }
  }

  getConsultationStatusText(status: number): string {
    switch (status) {
      case 0:
        return 'consultationStatus.pending';
      case 1:
        return 'consultationStatus.answered';
      case 2:
        return 'consultationStatus.completed';

      default:
        return 'consultationStatus.unknown';
    }
  }

  onAssignToMe(event: Event): void {
    this.assignToMeClick.emit(this.item);
  }
}
